export const WIDGET_SRC =
	"https://widget.productlane.com/latest.productlane-widget.min.js"

const webBootstrap =
	';((w)=>{const P=(w.Productlane={queue:{}});["set","open","close","toggle","on","off","init","enable","disable"].forEach(m=>{P[m]=(n=>function(){P.queue[n]={args:arguments}})(m)})})(window);'

export type SnippetPosition = "left" | "right" | "center"
export type SnippetMode = "auto" | "light" | "dark"

export interface SnippetOptions {
	portalInstance?: string
	spotlight?: boolean
	position?: SnippetPosition
	mode?: SnippetMode
}

// What the widget does when a field is left out; a snippet only spells out the rest.
export const SNIPPET_DEFAULTS = {
	spotlight: false,
	position: "right",
	mode: "auto",
} as const satisfies Required<Omit<SnippetOptions, "portalInstance">>

export type WidgetType = "launcher" | "spotlight"

export const widgetTypeOf = (options: SnippetOptions): WidgetType =>
	options.spotlight ? "spotlight" : "launcher"

export const WIDGET_DOCS_URL = "https://productlane.com/docs/setup/widget"

/** Spotlight is built for a centered bar, so turning it on moves the default launcher there and off moves it back. */
export const nextSnippetOptions = (
	options: SnippetOptions,
	patch: Partial<SnippetOptions>,
): SnippetOptions => {
	const next = { ...options, ...patch }
	const position = options.position ?? SNIPPET_DEFAULTS.position
	if (patch.spotlight === true && position === SNIPPET_DEFAULTS.position) {
		next.position = "center"
	}
	if (patch.spotlight === false && position === "center") {
		next.position = SNIPPET_DEFAULTS.position
	}
	return next
}

/** The init fields in the order the snippet prints them, defaults left out. */
export const initFields = (widgetKey: string, options: SnippetOptions = {}) => {
	const fields: string[] = [`widgetKey: "${widgetKey}"`]
	if (options.portalInstance) {
		fields.push(`portalInstance: "${options.portalInstance}"`)
	}
	if (options.spotlight) fields.push("spotlight: true")
	if (options.position && options.position !== SNIPPET_DEFAULTS.position) {
		fields.push(`position: "${options.position}"`)
	}
	if (options.mode && options.mode !== SNIPPET_DEFAULTS.mode) {
		fields.push(`mode: "${options.mode}"`)
	}
	return fields
}

/** Init argument on one line. */
const initArg = (widgetKey: string, options?: SnippetOptions) =>
	`{ ${initFields(widgetKey, options).join(", ")} }`

/** Same argument spread over lines, for the snippets that already read that way. */
const initArgBlock = (
	widgetKey: string,
	options: SnippetOptions | undefined,
	indent: string,
) =>
	`{\n${initFields(widgetKey, options)
		.map((field) => `${indent}  ${field}`)
		.join(",\n")}\n${indent}}`

const mobileHtml = (
	widgetKey: string,
	options?: SnippetOptions,
) => `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
  </head>
  <body>
    <script>
      ${webBootstrap}

      Productlane.init(${initArg(widgetKey, options)})
    </script>
    <script async defer crossorigin="anonymous" src="${WIDGET_SRC}"></script>
  </body>
</html>`

export interface Snippet {
	id: string
	label: string
	language: string
	build: (widgetKey: string, options?: SnippetOptions) => string
}

export const SNIPPETS: Snippet[] = [
	{
		id: "html",
		label: "HTML",
		language: "xml",
		build: (widgetKey, options) => `<script>
  ${webBootstrap}

  Productlane.init(${initArgBlock(widgetKey, options, "  ")})
</script>

<script
  async
  defer
  crossorigin="anonymous"
  src="${WIDGET_SRC}"
>
</script>`,
	},
	{
		id: "react",
		label: "React",
		language: "javascript",
		build: (widgetKey, options) => `import { useEffect } from "react"

export function ProductlaneWidget() {
  useEffect(() => {
    ${webBootstrap}

    window.Productlane.init(${initArg(widgetKey, options)})

    const script = document.createElement("script")
    script.src = "${WIDGET_SRC}"
    script.async = true
    script.defer = true
    script.crossOrigin = "anonymous"
    document.body.appendChild(script)

    return () => script.remove()
  }, [])

  return null
}`,
	},
	{
		id: "nextjs",
		label: "Next.js",
		language: "javascript",
		build: (widgetKey, options) => `"use client"

import Script from "next/script"

export function ProductlaneWidget() {
  return (
    <>
      <Script id="productlane-init" strategy="afterInteractive">
        {\`${webBootstrap}
Productlane.init(${initArg(widgetKey, options)})\`}
      </Script>
      <Script
        src="${WIDGET_SRC}"
        strategy="afterInteractive"
        crossOrigin="anonymous"
      />
    </>
  )
}`,
	},
	{
		id: "vue",
		label: "Vue",
		language: "xml",
		build: (widgetKey, options) => `<script setup>
import { onMounted } from "vue"

onMounted(() => {
  ${webBootstrap}

  window.Productlane.init(${initArg(widgetKey, options)})

  const script = document.createElement("script")
  script.src = "${WIDGET_SRC}"
  script.async = true
  script.defer = true
  script.crossOrigin = "anonymous"
  document.body.appendChild(script)
})
</script>`,
	},
	{
		id: "react-native",
		label: "React Native",
		language: "javascript",
		build: (
			widgetKey,
			options,
		) => `import { WebView } from "react-native-webview"

// Runs the web widget inside a WebView.
const html = \`${mobileHtml(widgetKey, options)}\`

export function ProductlaneWidget() {
  return <WebView originWhitelist={["*"]} source={{ html }} />
}`,
	},
	{
		id: "swift",
		label: "Swift (iOS)",
		language: "swift",
		build: (widgetKey, options) => `import WebKit

// Runs the web widget inside a WKWebView.
let html = """
${mobileHtml(widgetKey, options)}
"""

let webView = WKWebView()
webView.loadHTMLString(html, baseURL: URL(string: "https://productlane.com"))`,
	},
	{
		id: "kotlin",
		label: "Kotlin (Android)",
		language: "kotlin",
		build: (widgetKey, options) => `// Runs the web widget inside a WebView.
val html = """
${mobileHtml(widgetKey, options)}
""".trimIndent()

val webView = WebView(context)
webView.settings.javaScriptEnabled = true
webView.loadDataWithBaseURL("https://productlane.com", html, "text/html", "utf-8", null)`,
	},
	{
		id: "flutter",
		label: "Flutter",
		language: "dart",
		build: (
			widgetKey,
			options,
		) => `import 'package:webview_flutter/webview_flutter.dart';

// Runs the web widget inside a WebView.
const html = '''
${mobileHtml(widgetKey, options)}
''';

final controller = WebViewController()
  ..setJavaScriptMode(JavaScriptMode.unrestricted)
  ..loadHtmlString(html);`,
	},
]

export const MAIN_PORTAL_OPTION = "__main__"
